import React from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Tabs, 
  Tab, 
  Switch, 
  FormControlLabel, 
  Paper,
  Divider
} from '@mui/material';
import CompanyProfileForm from '../components/company/CompanyProfileForm';
import FeedbackForm from '../components/feedback/FeedbackForm';
import TeamManagement from '../components/teams/TeamManagement';
import AutomationSettings from '../components/automation/AutomationSettings';
import UserManagement from '../components/users/UserManagement';
import StripePaymentsSettings from '../components/payments/StripePaymentsSettings';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { usePermissions } from '../hooks/usePermissions';
import GroupIcon from '@mui/icons-material/Group';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import PaymentIcon from '@mui/icons-material/Payment';
import PersonIcon from '@mui/icons-material/Person';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function Settings() {
  const [tabValue, setTabValue] = React.useState(0);
  const { currentUser } = useAuth();
  const { mode, toggleColorMode } = useThemeMode();
  const { canManageUsers, canManageSettings } = usePermissions();

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Build tabs array dynamically based on permissions
  const tabs = [];
  const tabPanels = [];

  // Company Settings Tab (always visible)
  tabs.push({ label: 'Company', icon: <BusinessIcon />, key: 'company' });
  
  // Team & Users Tab (always visible, but content varies)
  tabs.push({ label: 'Team & Users', icon: <PeopleIcon />, key: 'team' });
  
  // Personal Tab (always visible)
  tabs.push({ label: 'Personal', icon: <PersonIcon />, key: 'personal' });

  // Build tab panels with consistent indices
  let tabIndex = 0;

  // Company Settings Panel
  tabPanels.push(
    <TabPanel key="company" value={tabValue} index={tabIndex}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Typography variant="h5" gutterBottom>
          Company Profile
        </Typography>
        <CompanyProfileForm />
        
        {canManageSettings && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Automation Settings
              </Typography>
              <AutomationSettings />
            </Box>
            
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Payment Settings
              </Typography>
              <StripePaymentsSettings />
            </Box>
          </>
        )}
      </Box>
    </TabPanel>
  );
  tabIndex++;

  // Team & Users Panel
  tabPanels.push(
    <TabPanel key="team" value={tabValue} index={tabIndex}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {canManageUsers && (
          <>
            <Typography variant="h5" gutterBottom>
              User Management
            </Typography>
            <UserManagement />
            <Divider sx={{ my: 2 }} />
          </>
        )}
        
        <Typography variant="h5" gutterBottom>
          Team Management
        </Typography>
        <TeamManagement />
      </Box>
    </TabPanel>
  );
  tabIndex++;

  // Personal Settings Panel
  tabPanels.push(
    <TabPanel key="personal" value={tabValue} index={tabIndex}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Typography variant="h5" gutterBottom>
          Account Information
        </Typography>
        <Paper sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>Email:</strong> {currentUser?.email}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your account email address. Contact your administrator to change this.
          </Typography>
        </Paper>
        
        <Divider />
        
        <Typography variant="h5" gutterBottom>
          Appearance
        </Typography>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {mode === 'dark' ? (
                <DarkModeIcon color="primary" />
              ) : (
                <LightModeIcon color="primary" />
              )}
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Dark Mode
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Switch between light and dark theme
                </Typography>
              </Box>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={mode === 'dark'}
                  onChange={toggleColorMode}
                  color="primary"
                />
              }
              label={mode === 'dark' ? 'On' : 'Off'}
              labelPlacement="end"
            />
          </Box>
        </Paper>

        <Divider />
        
        <Typography variant="h5" gutterBottom>
          Feedback
        </Typography>
        <Paper sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We'd love to hear your feedback to help improve Site Sync!
          </Typography>
          <FeedbackForm />
        </Paper>
      </Box>
    </TabPanel>
  );
  tabIndex++;

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage your company settings, team, and personal preferences
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          {tabs.map((tab, index) => (
            <Tab
              key={tab.key}
              icon={tab.icon}
              iconPosition="start"
              label={tab.label}
              sx={{ minHeight: 64 }}
            />
          ))}
        </Tabs>
      </Box>

      {tabPanels}
    </Container>
  );
} 